import { HgSupportedLanguage } from '@smg-real-estate/domus';
import { createI18n, type I18n, type LocaleMessages } from 'vue-i18n';
import { type RouteLocationNormalized, type Router } from 'vue-router';

import { isPlatformIs24 } from '@/helpers/platform';
import { captureError } from '@smg-real-estate/browser-rum';
import { setupRumViewTracking } from '@smg-real-estate/browser-rum/vue';

export class AppI18n {
  public defaultLanguage: HgSupportedLanguage;
  public i18nInstance: I18n;
  private readonly localePlatform: string;

  constructor(locale?: HgSupportedLanguage, defaultTranslations?: LocaleMessages<any>) {
    this.defaultLanguage = locale ?? HgSupportedLanguage.DE;
    this.localePlatform = isPlatformIs24() ? 'is24' : 'hg';
    this.i18nInstance = createI18n({
      locale: this.defaultLanguage,
      legacy: false,
      fallbackLocale: HgSupportedLanguage.DE,
      fallbackWarn: true,
      datetimeFormats: {},
      warnHtmlMessage: false,
      missing: (currentLocale: string, key: string) =>
        console.warn('Missing translation', { key, currentLocale }),
      messages: {
        [this.defaultLanguage]: defaultTranslations || {},
      },
    });
  }

  private async loadTranslations(locale: HgSupportedLanguage): Promise<void> {
    try {
      const appTranslations = await import(`@/i18n/${locale}-${this.localePlatform}.ts`);
      const appLocales = appTranslations.default;
      this.i18nInstance.global.mergeLocaleMessage(locale, appLocales);
    } catch (e) {
      captureError(e);
    }
  }

  public async setLocaleBeforeRouteChange(locale: HgSupportedLanguage): Promise<void> {
    const currentMessages = this.i18nInstance.global.getLocaleMessage(locale);
    if (Object.keys(currentMessages).length === 0) {
      await this.loadTranslations(locale);
    }
  }

  public async routerBeforeEachGuard(
    to: RouteLocationNormalized,
    _from: RouteLocationNormalized,
    next: () => void,
  ): Promise<void> {
    const locale = to?.meta?.locale ?? to?.params?.locale ?? this.defaultLanguage;
    await this.setLocaleBeforeRouteChange(locale as HgSupportedLanguage);
    next();
  }

  public addRouterHook(router: Router): void {
    router.beforeEach(this.routerBeforeEachGuard.bind(this));
    setupRumViewTracking(router);
  }
}
